import { LoadingButton } from "@mui/lab";
import { Box, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import logo from "../assets/Logo.png";
import { signInWithEmailPassword } from "../firebase";
import { getContent } from "../store";
import {
	dashOutlinedTextFieldLargeSx,
	dashPrimaryGradientButtonSx,
} from "../utils/muiDashboardSx";
import { DASHBOARD_PALETTE as dash } from "../utils/palette.token";

interface IFormInput {
	email: string;
	password: string;
}

function Login() {
	const navigate = useNavigate();
	const content = useSelector(getContent);
	const [loading, setLoading] = useState<boolean>(false);

	const { register, handleSubmit } = useForm<IFormInput>();

	const onSubmit: SubmitHandler<IFormInput> = async ({ email, password }) => {
		setLoading(true);
		await signInWithEmailPassword(email, password, content);
		setLoading(false);
		navigate("/");
	};

	const fieldSxLarge = dashOutlinedTextFieldLargeSx(dash);
	// App main uses p:0 on login route — full viewport height
	const viewportSlot = "100dvh";

	return (
		<Box
			sx={{
				minHeight: viewportSlot,
				maxHeight: viewportSlot,
				width: "100%",
				boxSizing: "border-box",
				bgcolor: dash.bg,
				color: dash.textPrimary,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				position: "relative",
				overflow: "hidden",
				px: 3,
				py: 0,
			}}
		>
			<Box
				aria-hidden
				sx={{
					position: "absolute",
					top: { xs: "-15%", md: "-10%" },
					right: { xs: "-25%", md: "-10%" },
					width: { xs: 280, md: 420 },
					height: { xs: 280, md: 420 },
					borderRadius: "50%",
					background: dash.glowOrbAccent,
					pointerEvents: "none",
					opacity: 0.65,
				}}
			/>
			<Box
				aria-hidden
				sx={{
					position: "absolute",
					bottom: { xs: "-20%", md: "-15%" },
					left: { xs: "-30%", md: "-15%" },
					width: { xs: 260, md: 360 },
					height: { xs: 260, md: 360 },
					borderRadius: "50%",
					background: dash.glowOrbSuccess,
					pointerEvents: "none",
					opacity: 0.45,
				}}
			/>

			<Box
				sx={{
					width: "100%",
					maxWidth: { xs: 520, sm: 600, md: 640 },
					maxHeight: "100%",
					/** Tránh card quá thấp so với chiều ngang (tỉ lệ “dẹt”) */
					minHeight: { xs: 360, sm: 460, md: 480 },
					position: "relative",
					zIndex: 1,
					borderRadius: 3,
					border: `1px solid ${dash.cardBorder}`,
					background: dash.gradientSurface,
					boxShadow: `0 24px 64px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(100, 160, 255, 0.06) inset`,
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						height: 3,
						flexShrink: 0,
						background: `linear-gradient(90deg, ${dash.accent}, ${dash.success})`,
						opacity: 0.9,
					}}
				/>
				<Box
					sx={{
						p: { xs: 3.5, sm: 4.5, md: 5 },
						flex: "1 1 auto",
						minHeight: 0,
						overflow: "auto",
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
					}}
				>
					<Box
						component="img"
						alt="talentIDO"
						src={logo}
						sx={{
							display: "block",
							mx: "auto",
							maxWidth: { xs: 240, sm: 280, md: 300 },
							width: "100%",
							height: "auto",
							objectFit: "contain",
						}}
					/>
					<Typography
						variant="subtitle2"
						align="center"
						className="font-montserrat600"
						sx={{
							color: dash.textSecondary,
							mt: 2.5,
							mb: 3,
							letterSpacing: "0.18em",
							textTransform: "uppercase",
							fontSize: "0.8125rem",
						}}
					>
						Admin Dashboard
					</Typography>

					<Box
						component="form"
						onSubmit={handleSubmit(onSubmit)}
						sx={{ display: "flex", flexDirection: "column", gap: 2.75 }}
						noValidate
					>
						<TextField
							{...register("email", { required: true, maxLength: 100 })}
							type="email"
							autoComplete="email"
							placeholder="Email"
							fullWidth
							variant="outlined"
							size="medium"
							sx={fieldSxLarge}
						/>
						<TextField
							{...register("password", { required: true, maxLength: 50 })}
							type="password"
							autoComplete="current-password"
							placeholder="Password"
							fullWidth
							variant="outlined"
							size="medium"
							sx={fieldSxLarge}
						/>
						<LoadingButton
							loading={loading}
							type="submit"
							fullWidth
							variant="contained"
							disableElevation
							sx={{
								...dashPrimaryGradientButtonSx(dash),
								mt: 1,
								py: 1.65,
								textTransform: "uppercase",
								letterSpacing: "0.1em",
								fontSize: "0.9rem",
							}}
						>
							Login
						</LoadingButton>
					</Box>
				</Box>
			</Box>
		</Box>
	);
}

export default Login;
